<template>
  <dn-collection
    :cid="placeholderId"
    class="dn-most-read-collection"
    :provider="provider"
    :options="{ streamId, size, removeUsed: false }"
  >
    <template #default="{ collection, loading }">
      <dn-headline
        v-if="headline"
        class="header"
        :text="headline"
      />
      <dn-grid-layout
        v-if="loading || collection.length > 0"
        :pattern="rowlayout.toLowerCase()"
        :repeat="1"
        :items="loading ? skeletonItems : collection"
      >
        <template #default="{ item, itemIndex }">
          <dn-most-read-card
            :id="item?.id || item"
            :skeleton="loading"
            :data_attr="itemIndex + 1"
            :prefetch="true"
          />
        </template>
      </dn-grid-layout>
    </template>
  </dn-collection>
</template>
<script lang="ts" setup>
import { provide } from 'vue';

const props = defineProps({
  placeholder: {
    type: String,
    required: true
  },
  streamId: {
    type: String,
    required: true
  },
  headline: {
    type: String,
    required: false,
    default: null
  },
  rowlayout: {
    type: String,
    required: false,
    default: 'BBBBBB'
  },
  id: {
    type: String,
    required: true
  },
  removeUsed: {
    type: Boolean,
    required: false,
    default: true
  }
});

provide('teaser_provider', 'articleTeaser');
const provider = 'kilkaya'; // TODO: This needs to be taken from config
const size = props.rowlayout.replace(/\+/g, '').length;
const view = props.rowlayout ? `layout-${props.rowlayout.toLowerCase()}` : null;
const placeholderId = `${props.placeholder}__${props.streamId}__${props.id}`;
const skeletonItems = Array.from({ length: size }, (_, index) => ({ id: `skeleton-${index}` }));
</script>

<style lang="scss">
.dn-most-read-collection {
  --dn-collection_gap: var(--ui-gap);
  --dn-collection--header_border-bottom-width: 1px;
  --dn-collection--header_border-bottom-color: var(--color-ui-generic-divider);
  --dn-collection--header_spacing: 0 0 var(--ui-gap);
  --dn-collection_spacing: var(--row-gap);
  --dn-collection_fill: var(--color-ui-generic-bg);
  --dn-grid_column-gap: 0;

  @media only screen and (width >= 980px) {
    --dn-grid_row-gap: 0;
  }

  .header {
    font-family: var(--typography-mostread-heading-min-font-family);
    font-size: var(--typography-mostread-heading-min-font-size);
    line-height: var(--typography-mostread-heading-min-line-height);
    font-weight: var(--typography-mostread-heading-min-font-weight);

    @media only screen and (width >= 980px) {
      font-family: var(--typography-mostread-heading-max-font-family);
      font-size: var(--typography-mostread-heading-max-font-size);
      line-height: var(--typography-mostread-heading-max-line-height);
      font-weight: var(--typography-mostread-heading-max-font-weight);
    }
  }

  .layout-bbbbbb {
    --dn-grid_grid-template: auto / repeat(1, 1fr);

    @media only screen and (width >= 980px) {
      --dn-grid_grid-template: auto / repeat(3, 1fr);
    }

    > .dn-most-read-card {
      --dn-most-read--link-color: var(--color-teaser-standard-fg);
      --dn-most-read--card_border-bottom-color: var(--color-ui-generic-divider);
      --dn-most-read--card_padding: 0 0 var(--row-gap);
      --dn-most-read--card_font-family: var(--typography-teaser-default-b-large-max-font-family);
      --dn-most-read--card_grid-template: 1fr / 20px repeat(11, 1fr);
      --dn-most-read--card_title_grid-area: 1 / 2 / span 1 / 12;
      --dn-most-read--card_meta_grid-area: auto / 2 / span 1 / 12;
      --dn-most-read--meta_font-family: var(--typography-utility-topic-font-family);
      --dn-most-read--meta_color: var(--color-teaser-standard-subtle-fg);
      --dn-skeleton_lines_border-bottom-color: var(--color-ui-generic-divider);
      --dn-skeleton_lines_padding: 0 0 var(--ui-gap);

      @media only screen and (width >= 980px) {
        --dn-most-read--card_border-right-width: 1px;
        --dn-most-read--card_border-right-color: var(--color-ui-generic-divider);
        --dn-most-read--meta_spacing: var(--row-gap-section) 0 0;
        --dn-skeleton_lines_border-right-width: 1px;
        --dn-skeleton_lines_border-right-color: var(--color-ui-generic-divider);

        &:nth-child(-n + 3) {
          --dn-most-read--card_padding: 0 0 var(--row-gap);
          --dn-skeleton_lines_padding: 0 var(--row-gap) var(--row-gap) 0;
        }

        &:nth-last-child(-n + 3) {
          --dn-most-read--card_border-bottom-width: 0;
          --dn-most-read--card_padding: var(--row-gap) 0 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: var(--row-gap) var(--row-gap) 0 0;
        }

        &:nth-child(2),
        &:nth-child(3) {
          --dn-most-read--card_padding: 0 0 var(--row-gap) var(--row-gap);
          --dn-skeleton_lines_padding: 0 var(--row-gap) var(--row-gap);
        }

        &:nth-child(5),
        &:nth-child(6) {
          --dn-most-read--card_padding: var(--row-gap) 0 0 var(--row-gap);
          --dn-skeleton_lines_padding: var(--row-gap) var(--row-gap) 0 var(--row-gap);
        }
      }

      &:nth-child(3),
      &:nth-last-child(1) {
        --dn-most-read--card_border-right-width: 0;
        --dn-skeleton_lines_border-right-width: 0;
      }

      &:nth-last-child(1) {
        --dn-most-read--card_border-bottom-width: 0;
        --dn-skeleton_lines_border-bottom-width: 0;
      }
    }
  }

  .dn-card {
    .title {
      --dn-headline_font-family: var(--typography-mostread-title-min-font-family);
      --dn-headline_font-size: var(--typography-mostread-title-min-font-size);
      --dn-headline_line-height: var(--typography-mostread-title-min-line-height);
      --dn-headline_font-weight: var(--typography-mostread-title-min-font-weight);

      @media only screen and (width >= 980px) {
        --dn-headline_font-family: var(--typography-mostread-title-max-font-family);
        --dn-headline_font-size: var(--typography-mostread-title-max-font-size);
        --dn-headline_line-height: var(--typography-mostread-title-max-line-height);
        --dn-headline_font-weight: var(--typography-mostread-title-max-font-weight);
      }
    }

    &::before {
      content: attr(data-attr);
      font-family: var(--typography-mostread-number-min-font-family);
      font-size: var(--typography-mostread-number-min-font-size);
      line-height: var(--typography-mostread-number-min-line-height);
      font-weight: var(--typography-mostread-number-min-font-weight);

      @media only screen and (width >= 980px) {
        font-family: var(--typography-mostread-number-max-font-family);
        font-size: var(--typography-mostread-number-max-font-size);
        line-height: var(--typography-mostread-number-max-line-height);
        font-weight: var(--typography-mostread-number-max-font-weight);
      }
    }
  }
}
</style>
